import { QuestionnaireAnswer, RiskIndicatorType, RiskMetric, TaskTypeEnum } from '@api';
import { DropdownItemType } from '@components/ui';
import { depaginate, mapExistingQuestionnaire, useApi } from '@data-access';
import { hasStyleMetric, STYLE_TASK_TYPES } from '@lib/styleMetric';
import { initialState, questionnaireStore, store } from '@stores';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from './useTestSection.module.scss';

dayjs.extend(relativeTime);

const textGenerationSpecialMetrics = ['Personal Data Exposure', 'Criteria Insightfulness', 'Key Takeaway Score'];

const buildDropdownLabel = (answer: QuestionnaireAnswer) => (
  <div className={styles.selectedValue}>
    <div className={styles.selectedName}>
      <span>
        {answer.assessments[0]?.id ? `Executed Assessment ID - ${answer.assessments[0].id}` : 'Draft Assessment'}
      </span>
    </div>
    <div className={styles.dropdownTime}>({dayjs().to(answer.timestamp)})</div>
  </div>
);

const getUniqueById = <T extends { id: number }>(items: T[] = []): T[] => {
  const seen = new Set<number>();

  return items.filter(item => {
    if (seen.has(item.id)) {
      return false;
    }

    seen.add(item.id);
    return true;
  });
};

const orderRiskIndicators = (items: RiskIndicatorType[] = []) => {
  const orderedNames = ['performance', 'fairness', 'robustness', 'explainability'];
  const uniqueItems = getUniqueById(items);

  return orderedNames.flatMap(name => uniqueItems.filter(indicator => indicator.name === name));
};

export const useTestSection = () => {
  const {
    latestAssessment,
    questionnaireData,
    questionnaireAnswers,
    riskMetrics,
    riskIndicators,
    selectedRiskMetrics,
    selectedQuestionnaireAnswersOption,
    addQuestionnaireData,
    setLatestAssessment,
    setMaxStep,
    setRiskMetrics,
    setRiskIndicators,
    setSelectedRiskMetrics,
    setSelectedQuestionnaireAnswersOption,
    setQuestionnaireData,
    setQuestionnaireAnswers,
  } = questionnaireStore;

  const { loader, setLoader } = store;
  const { api, questionnaireApi } = useApi();
  const { query } = useRouter();

  const [isInitialized, setIsInitialized] = useState(false);
  const initializationStartedRef = useRef(false);
  const fetchRiskMetricsRequestIdRef = useRef(0);

  // Must stay in sync with `stepIds` in useDataCollectionQuestionnaire
  const hasStyleStep = STYLE_TASK_TYPES.includes(questionnaireData?.taskType) && hasStyleMetric(selectedRiskMetrics);

  const isDirectSubmit =
    !hasStyleStep &&
    (questionnaireData?.taskType === TaskTypeEnum.singleTurnChat ||
      questionnaireData?.taskType === TaskTypeEnum.informationExtraction ||
      questionnaireData?.taskType === TaskTypeEnum.conversationalAgent ||
      questionnaireData?.taskType === TaskTypeEnum.voiceAgent ||
      (questionnaireData?.taskType === TaskTypeEnum.textGeneration &&
        !selectedRiskMetrics.some(metric => textGenerationSpecialMetrics.includes(metric.name))) ||
      (questionnaireData?.taskType === TaskTypeEnum.tabularMl &&
        selectedRiskMetrics.every(metric => metric.indicator.name === 'explainability')));

  const availableRiskMetrics = useMemo(
    () => getUniqueById(riskMetrics?.filter(m => m.availableForAssessment !== false) ?? []),
    [riskMetrics],
  );

  // `allMetrics` lets callers pass a freshly-fetched risk metrics list instead of relying on
  // `riskMetrics` state, which may not have re-rendered yet right after `fetchRiskMetrics` resolves.
  const filterAvailableRiskMetrics = useCallback(
    (metrics: RiskMetric[] = [], allMetrics: RiskMetric[] = riskMetrics ?? []) => {
      const ids = new Set(getUniqueById(allMetrics.filter(m => m.availableForAssessment !== false)).map(m => m.id));
      return getUniqueById(metrics).filter(metric => ids.has(metric.id));
    },
    [riskMetrics],
  );

  const resetQuestionnaireData = () =>
    // reset questionnaire inputs (except task type, model name, use case name, model id, use case id, data pipeline)
    setQuestionnaireData({
      ...initialState,
      taskType: questionnaireData.taskType,
      modelName: questionnaireData.modelName,
      useCaseName: questionnaireData.useCaseName,
      modelId: questionnaireData.modelId,
      useCaseId: questionnaireData.useCaseId,
      dataPipeline: questionnaireData.dataPipeline,
    });

  // metrics selection logic
  const hasAllMetricsSelected = (indicator: number): boolean => {
    const availablePerIndicator = availableRiskMetrics.filter(m => m.indicator.id === indicator);
    if (availablePerIndicator.length === 0) return false;
    const selectedPerIndicator = selectedRiskMetrics.filter(m => m.indicator.id === indicator);
    return availablePerIndicator.length === selectedPerIndicator.length;
  };

  const hasAnyMetricsSelected = (indicator: number): boolean =>
    selectedRiskMetrics.some(m => m.indicator.id === indicator);

  const getSelectedMetricsPerIndicator = (indicator: number): RiskMetric[] =>
    selectedRiskMetrics.filter(m => m.indicator.id === indicator);

  const getAvailableMetricsPerIndicator = (indicator: number): RiskMetric[] =>
    availableRiskMetrics.filter(m => m.indicator.id === indicator);

  const handleIndicatorSelect = (indicator: number) => {
    const availablePerIndicator = availableRiskMetrics.filter(m => m.indicator.id === indicator);
    const selectedPerIndicator = selectedRiskMetrics.filter(m => m.indicator.id === indicator);
    if (selectedPerIndicator.length > 0) {
      if (hasAllMetricsSelected(indicator)) {
        setSelectedRiskMetrics(selectedRiskMetrics.filter(m => m.indicator.id !== indicator));
      } else {
        setSelectedRiskMetrics(filterAvailableRiskMetrics([...selectedRiskMetrics, ...availablePerIndicator]));
      }
    } else {
      setSelectedRiskMetrics(filterAvailableRiskMetrics([...selectedRiskMetrics, ...availablePerIndicator]));
    }
  };

  const handleMetricSelect = (metric: RiskMetric) => {
    if (metric.availableForAssessment === false) return;

    if (selectedRiskMetrics.find(m => m.id === metric?.id)) {
      setSelectedRiskMetrics(selectedRiskMetrics.filter(m => m.id !== metric.id));
    } else {
      setSelectedRiskMetrics(filterAvailableRiskMetrics([...selectedRiskMetrics, metric]));
    }
  };

  const handleSelectAll = () => {
    if (selectedRiskMetrics.length === availableRiskMetrics.length) {
      setSelectedRiskMetrics([]);
    } else {
      setSelectedRiskMetrics(availableRiskMetrics);
    }
  };

  const { taskType } = questionnaireData;
  const datasetPipelineId = questionnaireData.dataPipeline?.id;

  // fetch risk metrics and indicators
  const fetchRiskMetrics = useCallback(async () => {
    const requestId = fetchRiskMetricsRequestIdRef.current + 1;
    fetchRiskMetricsRequestIdRef.current = requestId;

    const fetchedRiskMetrics =
      taskType && datasetPipelineId
        ? getUniqueById(
            await depaginate(page =>
              api.apiRiskMetricsList({
                page,
                taskType,
                datasetPipelineId,
                isActive: true,
              }),
            ),
          )
        : [];

    const fetchedRiskIndicators = orderRiskIndicators(
      await depaginate(page => api.apiRiskIndicatorTypesList({ page, compact: true })),
    );

    if (requestId !== fetchRiskMetricsRequestIdRef.current) {
      return [];
    }

    setRiskMetrics(fetchedRiskMetrics);
    setRiskIndicators(fetchedRiskIndicators);
    return fetchedRiskMetrics;
  }, [api, taskType, datasetPipelineId, setRiskIndicators, setRiskMetrics]);

  // Fetches the previous assessment purely for display (e.g. per-metric prior-result status
  // icons) - `selectedRiskMetrics` prefill doesn't depend on this, see callers below.
  const fetchLatestAssessment = useCallback(
    async (id: number) => {
      try {
        const fetchedLatestAssessment = await api.apiAssessmentsRetrieve({ id });
        setLatestAssessment(fetchedLatestAssessment);
      } catch {
        setLatestAssessment(null);
      }
    },
    [api, setLatestAssessment],
  );

  // fetch questionnaire answers
  const fetchQuestionnaireAnswers = useCallback(
    async (freshRiskMetrics?: RiskMetric[]) => {
      if (!questionnaireData.modelName || !questionnaireData.dataPipeline?.id) return;
      const fetchedQuestionnaireAnswers = await depaginate(page =>
        questionnaireApi.apiQuestionnaireAnswersList({ page, datasetPipeline: questionnaireData.dataPipeline?.id }),
      );

      setQuestionnaireAnswers(fetchedQuestionnaireAnswers);

      if (query?.assessment) return;

      const mappedAnswers = fetchedQuestionnaireAnswers.map(answer => ({
        label: buildDropdownLabel(answer),
        value: answer.id,
      }));

      setSelectedQuestionnaireAnswersOption(mappedAnswers[0]);

      if (fetchedQuestionnaireAnswers.length > 0) {
        const questionnaire = mapExistingQuestionnaire(fetchedQuestionnaireAnswers[0]);
        questionnaire.modelName = questionnaireData.modelName;
        questionnaire.useCaseName = questionnaireData.useCaseName;
        addQuestionnaireData({ ...questionnaire });
      }

      // Prefill straight from the answer - doesn't need the assessment fetch below to resolve.
      setSelectedRiskMetrics(
        filterAvailableRiskMetrics(fetchedQuestionnaireAnswers[0]?.selectedRiskMetrics ?? [], freshRiskMetrics),
      );

      if (fetchedQuestionnaireAnswers[0]?.assessments[0]?.id) {
        fetchLatestAssessment(fetchedQuestionnaireAnswers[0].assessments[0].id);
      } else {
        setLatestAssessment(null);
      }
    },
    [
      addQuestionnaireData,
      fetchLatestAssessment,
      filterAvailableRiskMetrics,
      query?.assessment,
      questionnaireApi,
      questionnaireData,
      setLatestAssessment,
      setQuestionnaireAnswers,
      setSelectedQuestionnaireAnswersOption,
      setSelectedRiskMetrics,
    ],
  );

  const handleQuestionnaireAnswerSelect = (item: DropdownItemType) => {
    const selectedQuestionnaireAnswer = questionnaireAnswers?.find(qa => qa.id === item.value);
    if (selectedQuestionnaireAnswer) {
      setSelectedQuestionnaireAnswersOption({
        label: buildDropdownLabel(selectedQuestionnaireAnswer),
        value: selectedQuestionnaireAnswer.id,
      });
      setSelectedRiskMetrics(filterAvailableRiskMetrics(selectedQuestionnaireAnswer.selectedRiskMetrics));

      // if it's not a draft assessment, fetch the latest assessment (for display only)
      if (selectedQuestionnaireAnswer.assessments[0]?.id) {
        fetchLatestAssessment(selectedQuestionnaireAnswer.assessments[0].id);
      } else {
        setLatestAssessment(null);
      }
      const questionnaire = mapExistingQuestionnaire(selectedQuestionnaireAnswer);
      addQuestionnaireData({ ...questionnaire, id: selectedQuestionnaireAnswer.id });
    } else {
      setSelectedRiskMetrics([]);
      setLatestAssessment(null);
      setSelectedQuestionnaireAnswersOption(item);
      setMaxStep(1);
      resetQuestionnaireData();
    }
  };

  useEffect(() => {
    if (!riskMetrics) return;

    const availableSelected = filterAvailableRiskMetrics(selectedRiskMetrics);
    const hasSelectionChanged =
      availableSelected.length !== selectedRiskMetrics.length ||
      availableSelected.some((metric, index) => metric.id !== selectedRiskMetrics[index]?.id);

    if (hasSelectionChanged) {
      setSelectedRiskMetrics(availableSelected);
    }
  }, [filterAvailableRiskMetrics, riskMetrics, selectedRiskMetrics, setSelectedRiskMetrics]);

  useEffect(() => {
    if (isInitialized || initializationStartedRef.current) return;
    initializationStartedRef.current = true;
    setLoader(true);

    const initialize = async () => {
      try {
        const fetchedRiskMetrics = await fetchRiskMetrics();
        if (questionnaireAnswers === null) {
          await fetchQuestionnaireAnswers(fetchedRiskMetrics);
        }
      } finally {
        setIsInitialized(true);
        setLoader(false);
      }
    };

    initialize();
  }, [fetchQuestionnaireAnswers, fetchRiskMetrics, isInitialized, questionnaireAnswers, setLoader]);

  return {
    latestAssessment,
    questionnaireData,
    questionnaireAnswers,
    riskMetrics,
    riskIndicators,
    selectedRiskMetrics,
    selectedQuestionnaireAnswersOption,
    loader,
    availableRiskMetrics,
    isDirectSubmit,
    hasAllMetricsSelected,
    hasAnyMetricsSelected,
    getSelectedMetricsPerIndicator,
    getAvailableMetricsPerIndicator,
    handleIndicatorSelect,
    handleMetricSelect,
    handleSelectAll,
    handleQuestionnaireAnswerSelect,
  };
};
